import { Grid, useTheme } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useContext, useRef, useState } from 'react';
import * as yup from 'yup';
import FormInput from '../../SystemDesign/FormInput';
import Switch from '../../SystemDesign/Switch';
import { tokens } from '../../theme';
import MuiDialog from '../../SystemDesign/MuiDialog';
import AddIcon from '@mui/icons-material/Add';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { useDispatch } from 'react-redux';
import { ADD_QUIZ } from '../../Reducers/Actions';
import { useCreateQuizMutation, useGetPostesQuery } from '../../services/SkillsManagement';
import { AuthContext } from '../../Context/AuthContext';
import ColoredSelector from '../../SystemDesign/ColoredSelector';

const CreateQuiz = ({ open, onClose, onSubmit }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const { user: authUser } = useContext(AuthContext) || {};

  const formikRef = useRef(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const [createQuiz, { isLoading }] = useCreateQuizMutation();

  const initialValues = {
    title: '',
    description: '',
    timeLimit: 30,
    maxAttempts: 3,
    shuffleQuestions: true,
    shuffleOptions: true,
    isActive: true,
    posteId: '',
  };

  // Local selector for Postes using ColoredSelector
  const PosteSelector = ({ formik }) => {
    const { data, isLoading, error } = useGetPostesQuery(
      {
        filters: "",
        sorting: "",
        pagination: JSON.stringify({ currentPage: 1, rowsPerPage: 100 })
      },
      { refetchOnMountOrArgChange: false }
    );

    const rawOptions = Array.isArray(data?.postes)
      ? data.postes
      : Array.isArray(data)
      ? data
      : [];

    // Normalize to ensure id and name keys exist
    const options = rawOptions.map((p) => ({
      id: p.id ?? p.posteId ?? p.postId ?? p.value,
      name: p.name ?? p.title ?? p.label ?? p.posteName ?? '',
    })).filter((p) => p.id != null && String(p.name).trim() !== '');

    return (
      <ColoredSelector
        showColorDot={false}
        options={options}
        value={formik.values.posteId}
        onChange={(value) => formik.setFieldValue('posteId', value)}
        placeholder="Select a position"
        label="position"
        valueKey="id"
        labelKey="name"
        searchable={true}
        required={false}
        error={formik.touched.posteId && Boolean(formik.errors.posteId)}
        helperText={formik.touched.posteId && formik.errors.posteId}
        disabled={isLoading || options.length === 0}
      />
    );
  };

  const [formValues, setFormValues] = useState(initialValues);

  const formFields = [
    { type: 'selectPoste', name: 'posteId', label: 'Position', required: false },
    { type: 'text', name: 'title', label: 'Quiz Title', required: true },
    { type: 'number', name: 'timeLimit', label: 'Time Limit (minutes)', required: true },
    { type: 'number', name: 'maxAttempts', label: 'Maximum Attempts', required: true },
    { type: 'textarea', name: 'description', label: 'Description', required: false, rows: 3 },
    { type: 'switch', name: 'isActive', label: 'Quiz Active', required: true },
    { type: 'switch', name: 'shuffleQuestions', label: 'Shuffle Questions', required: true },
    { type: 'switch', name: 'shuffleOptions', label: 'Shuffle Options', required: true },
  ];

  function getFieldGridProps(field) {
    switch (field.name) {
      case 'title':
      case 'maxAttempts':
      case 'timeLimit':
        return { xs: 12, md: 4, lg: 4 };
      case 'posteId':
        return { xs: 12, md: 12, lg: 12 };
      case 'description':
        return { xs: 12, md: 12, lg: 12 };
      case 'isActive':
      case 'shuffleQuestions':
      case 'shuffleOptions':
        return { xs: 12, md: 4, lg: 4 };
      default:
        return { xs: 12, md: 12, lg: 12 };
    }
  }

  const formValidationSchema = yup.object().shape({
    title: yup.string().required('Title is required'),
    description: yup.string().nullable(),
    timeLimit: yup
      .number()
      .typeError('Time limit must be a number')
      .min(0, 'Must be >= 0')
      .required('Time limit is required'),
    maxAttempts: yup
      .number()
      .typeError('Max attempts must be a number')
      .min(1, 'Must be >= 1')
      .required('Max attempts is required'),
    shuffleQuestions: yup.boolean().required(),
    shuffleOptions: yup.boolean().required(),
    isActive: yup.boolean().required(),
    posteId: yup.mixed().nullable(),
  });

  const handleChange = (e, formik) => {
    const { name, value, type } = e.target;
    if (type === 'number') {
      formik.setFieldValue(name, value === '' ? '' : Number(value));
    } else {
      formik.setFieldValue(name, value);
    }
  };

  const handleSubmit = async (values, actions) => {
    try {
      const createdByCandidate = authUser?.id || authUser?.userId || authUser?.uid || 0;
      const payload = {
        createdBy: Number(createdByCandidate) || 0,
        title: values.title.trim(),
        description: values.description?.trim() || '',
        timeLimit: Number(values.timeLimit || 0),
        maxAttempts: Number(values.maxAttempts || 1),
        shuffleQuestions: Boolean(values.shuffleQuestions),
        shuffleOptions: Boolean(values.shuffleOptions),
        isActive: Boolean(values.isActive),
        ...(values.posteId ? { posteId: Number(values.posteId) } : {}),
      };

      const result = await createQuiz({ body: payload }).unwrap();
      const quizObj = result?.data?.quiz || result?.quiz || result || {};
      // Try to robustly extract a numeric id from various possible response shapes
      const idCandidates = [
        quizObj?.quizId,
        quizObj?.id,
        result?.data?.quizId,
        result?.data?.id,
        result?.quizId,
        result?.id,
      ];
      const numericQuizId = idCandidates.map((v) => Number(v)).find((n) => Number.isFinite(n));

      const quizForStore = {
        ...payload,
        ...(Number.isFinite(numericQuizId) ? { quizId: numericQuizId } : {}),
        createdAt: quizObj.createdAt || new Date().toISOString(),
        updatedAt: quizObj.updatedAt || new Date().toISOString(),
      };

      if (Number.isFinite(numericQuizId)) {
        dispatch({ type: ADD_QUIZ, payload: quizForStore });
      }
      onSubmit?.(quizForStore);
      setInfoAlert({ severity: 'success', message: 'Quiz successfully created' });
      setOpenSnackBar(true);
      actions.resetForm();
      setFormValues(initialValues);
      // notify quizzes list to refresh
      try { window.dispatchEvent(new CustomEvent('quizzes:created')); } catch {}
      onClose?.();
    } catch (error) {
      console.error('Create quiz error:', error);
      setInfoAlert({ severity: 'error', message: error?.data?.message || 'Failed to create quiz' });
      setOpenSnackBar(true);
    }
    actions.setSubmitting(false);
  };

  const dialogContent = (
    <Formik
      initialValues={formValues}
      validationSchema={formValidationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize={true}
      onSubmit={handleSubmit}
      innerRef={formikRef}
    >
      {(formik) => {
        const { isValid, dirty } = formik;
        if (isFormValid !== isValid || isFormDirty !== dirty) {
          setTimeout(() => {
            setIsFormValid(isValid);
            setIsFormDirty(dirty);
          }, 0);
        }
        return (
          <Form id="create-quiz-form" autoComplete="off">
            <Grid container spacing={2}>
              {formFields.map((field, index) => (
                <Grid item size={{ ...getFieldGridProps(field) }} key={index}>
                  {field.type === 'selectPoste' && (
                    <PosteSelector
                      formik={formik}
                    />
                  )}
                  {field.type === 'text' && (
                    <FormInput
                      type="text"
                      name={field.name}
                      label={field.label}
                      required={field.required}
                      value={formik.values[field.name]}
                      onChange={(e) => handleChange(e, formik)}
                      onBlur={formik.handleBlur}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  {field.type === 'number' && (
                    <FormInput
                      type="number"
                      name={field.name}
                      label={field.label}
                      required={field.required}
                      value={formik.values[field.name]}
                      onChange={(e) => handleChange(e, formik)}
                      onBlur={formik.handleBlur}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  {field.type === 'textarea' && (
                    <FormInput
                      name={field.name}
                      label={field.label}
                      required={field.required}
                      value={formik.values[field.name]}
                      onChange={(e) => handleChange(e, formik)}
                      onBlur={formik.handleBlur}
                      placeholder={field.placeholder}
                      multiline
                      minRows={field.rows || 3}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                  {field.type === 'switch' && (
                    <Switch name={field.name} label={field.label} />
                  )}
                </Grid>
              ))}
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      icon={<AddIcon />}
      title="Create New Quiz"
      subtitle="Create a new quiz for skill evaluations."
      content={dialogContent}
      existAction={true}
      actionTitle="Create Quiz"
      actionFunction={() => formikRef.current?.handleSubmit()}
      isLoading={isLoading}
      actionType="submit"
      formId="create-quiz-form"
      disableAction={!(isFormValid && isFormDirty)}
    />
  );
};

export default CreateQuiz;
